import { useState } from 'react';
import { useGetPlatformCredentialsQuery } from '../../store/api/platformApi';
import Button from '../../reusable/Button/Button';
import CopyButton from '../../reusable/CopyButton/CopyButton';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import { t } from '../../i18n/i18n';
import './PlatformInfo.scss';

/**
 * The credentials section behind both TradeLocker Info and DX Trade Info.
 *
 * Extracted to the feature root the moment the second platform page needed it —
 * same rule `useCountdown` and `LeaderboardTable` follow in Competition. The
 * two pages differ only by which entry of `PLATFORMS` they hand in, and its
 * `id` is also the key the endpoint returns that platform under.
 */
export default function PlatformInfo({ platform }) {
  const { data, isLoading, isError, refetch } = useGetPlatformCredentialsQuery();
  const [revealed, setRevealed] = useState(false);

  const credentials = data?.[platform.id] ?? null;

  // DX Trade signs in without one, so the row is left out rather than shown
  // empty — same for anything else the platform doesn't issue.
  const rows = [
    { label: 'Server', value: credentials?.server },
    { label: 'Email', value: credentials?.email },
  ].filter((row) => row.value);

  return (
    <section className="profile-section platform-info">
      <div className="profile-head">
        <h2 className="profile-head__title">{t('{{platform}} Info', { platform: platform.name })}</h2>
        <p className="profile-head__desc">{t(platform.blurb)}</p>
      </div>

      {isLoading && <CredentialsSkeleton />}

      {!isLoading && credentials && (
        <article className="platform-card">
          <div className="platform-card__rows">
            {rows.map((row) => (
              <div key={row.label} className="platform-row">
                <span className="platform-row__label">{t(row.label)}</span>
                <span className="platform-row__value">{row.value}</span>
                <CopyButton value={row.value} label={t(row.label)} />
              </div>
            ))}

            {credentials.password && (
              <div className="platform-row">
                <span className="platform-row__label">{t('Password')}</span>
                <span className="platform-row__value platform-row__value--mono">
                  {revealed ? credentials.password : '•'.repeat(credentials.password.length)}
                </span>
                <button
                  type="button"
                  className="platform-row__reveal"
                  aria-pressed={revealed}
                  onClick={() => setRevealed(!revealed)}
                >
                  {revealed ? t('Hide') : t('Show')}
                </button>
                <CopyButton value={credentials.password} label={t('Password')} />
              </div>
            )}
          </div>
        </article>
      )}

      {!isLoading && !credentials && (
        <div className="platform-info__empty">
          <span>
            {isError
              ? t('We could not load your {{platform}} credentials.', { platform: platform.name })
              : t('No {{platform}} account on this profile yet. Credentials appear here as soon as an account is issued.', { platform: platform.name })}
          </span>
          {isError && (
            <button type="button" className="platform-info__retry" onClick={refetch}>
              {t('Try again')}
            </button>
          )}
        </div>
      )}

      <span className="profile-rule" />

      <div className="profile-actions">
        <span className="profile-note platform-info__note">
          {t('Platform passwords are issued with the account. Reset one from the terminal, not here.')}
        </span>
        <Button variant="submit" href={platform.web} target="_blank" rel="noreferrer">
          {t('Open {{platform}}', { platform: platform.name })}
        </Button>
      </div>
    </section>
  );
}

/** The card at its real size, with stand-ins for the three rows. */
function CredentialsSkeleton() {
  return (
    <article className="platform-card">
      <div className="platform-card__rows">
        {[0, 1, 2].map((row) => (
          <div key={row} className="platform-row">
            <Skeleton w={44} h={9} />
            <Skeleton w={168} h={9} />
          </div>
        ))}
      </div>
    </article>
  );
}
